import { useMutation, useQuery } from '@tanstack/react-query';
import secureLocalStorage from 'react-secure-storage';

import { serverRequest, serverRequest2 } from "../../util/server_request";
import { extractErrorMessage } from '../extractErrorMessage';
import { notifyUser, getUserDetail } from '../../util/util';

const notifiedErrors = new Set();

// Helper function to notify only once for the same error
const notifyOnce = (type, message, title = "") => {
  if (!notifiedErrors.has(message)) {
    notifiedErrors.add(message);
    notifyUser(type, message, title);
  }
};

const getMemberNumber = (explicitMemberNumber) => {
  const user = getUserDetail();
  return explicitMemberNumber || user?.member_number || '';
};

const getCurrentMVestAccount = async (memberNumber) => {
  const result = await serverRequest('GET', '/mvest/accounts');
  if (!result || !result.success) {
    throw new Error(result?.message || 'Failed to fetch MVest accounts');
  }

  const accounts = Array.isArray(result?.data) ? result.data : [];
  const targetMemberNumber = getMemberNumber(memberNumber);

  if (!targetMemberNumber) {
    throw new Error('Missing member number for MVest account');
  }

  const matchedAccount = accounts.find(
    (account) => account?.member_number === targetMemberNumber,
  );

  if (!matchedAccount?.id) {
    throw new Error('MVest account not found');
  }

  return matchedAccount;
};

// Create MVest account
export function useCreateMVestAccount() {
  return useMutation({
    mutationFn: async (accountData) => {
      try {
        const formData = new FormData();
        formData.append('mvest_plan', accountData.mvest_plan);
        formData.append('monthly_contribution', accountData.monthly_contribution);

        const result = await serverRequest2('POST', '/mvest-account/create', formData);
        if (!result || !result.success) {
          throw new Error(result?.message || 'Failed to create MVest account');
        }
        return result;
      } catch (error) {
        const errorMessage = extractErrorMessage(error, 'Failed to create MVest account');
        notifyOnce("error", errorMessage, "MVest Error");
        throw new Error(errorMessage);
      }
    },
  });
}

// Add beneficiary to MVest account
export function useAddMVestBeneficiary() {
  return useMutation({
    mutationFn: async (beneficiaryData) => {
      try {
        const memberNumber = getMemberNumber(beneficiaryData.member_number);
        const account = await getCurrentMVestAccount(memberNumber);
        const formData = new FormData();
        formData.append('member_number', memberNumber);
        formData.append('firstname', beneficiaryData.firstname);
        formData.append('othername', beneficiaryData.othername);
        formData.append('beneficiaryContact', beneficiaryData.beneficiaryContact);
        formData.append('percentageAllocation', beneficiaryData.percentageAllocation);
        formData.append('birth_date', beneficiaryData.birth_date);
        formData.append('relation', beneficiaryData.relation);

        const result = await serverRequest2('POST', `/mvest/add-beneficiary/${account.id}`, formData);
        if (!result || !result.success) {
          throw new Error(result?.message || 'Failed to add beneficiary');
        }
        return result;
      } catch (error) {
        const errorMessage = extractErrorMessage(error, 'Failed to add beneficiary');
        notifyOnce("error", errorMessage, "MVest Error");
        throw new Error(errorMessage);
      }
    },
  });
}

// Delete beneficiary from MVest account
export function useDeleteMVestBeneficiary() {
  return useMutation({
    mutationFn: async ({ beneficiaryId, memberNumber }) => {
      try {
        const targetMemberNumber = getMemberNumber(memberNumber);
        const endpoint = `/mvest/delete-beneficiary/${encodeURIComponent(beneficiaryId)}?member_number=${encodeURIComponent(targetMemberNumber)}`;
        const result = await serverRequest('DELETE', endpoint);
        if (!result || !result.success) {
          throw new Error(result?.message || 'Failed to delete beneficiary');
        }
        return result;
      } catch (error) {
        const errorMessage = extractErrorMessage(error, 'Failed to delete beneficiary');
        notifyOnce("error", errorMessage, "MVest Error");
        throw new Error(errorMessage);
      }
    },
  });
}

// Get beneficiaries for MVest account
export function useGetMVestBeneficiaries(enabled = true, memberNumber) {
  return useQuery({
    queryKey: ['mvest-beneficiaries', memberNumber],
    queryFn: async () => {
      try {
        const user = getUserDetail();
        const mn = memberNumber || user?.member_number || '';
        const endpoint = mn
          ? `/mvest/beneficiaries?member_number=${encodeURIComponent(mn)}`
          : '/mvest/beneficiaries';
        const result = await serverRequest('GET', endpoint);
        if (!result || !result.success) {
          throw new Error(result?.message || 'Failed to fetch beneficiaries');
        }
        return result;
      } catch (error) {
        const errorMessage = extractErrorMessage(error, 'Failed to fetch beneficiaries');
        const isNotFound = errorMessage.toLowerCase().includes('mvest account not found');
        const isFetchError = errorMessage.includes('Failed to fetch Mvest beneficiaries');
        if (!isNotFound && !isFetchError) {
          notifyOnce("error", errorMessage, "Error");
        }
        throw new Error(errorMessage);
      }
    },
    enabled: enabled && !!secureLocalStorage.getItem('token'),
    staleTime: 5 * 60 * 1000,
  });
}

// Update beneficiary in MVest account
export function useUpdateMVestBeneficiary() {
  return useMutation({
    mutationFn: async ({ beneficiaryId, beneficiaryData }) => {
      try {
        const memberNumber = getMemberNumber(beneficiaryData.member_number);
        const formData = new FormData();
        formData.append('member_number', memberNumber);
        formData.append('firstname', beneficiaryData.firstname);
        formData.append('othername', beneficiaryData.othername);
        formData.append('beneficiaryContact', beneficiaryData.beneficiaryContact);
        formData.append('percentageAllocation', beneficiaryData.percentageAllocation);
        formData.append('birth_date', beneficiaryData.birth_date);
        formData.append('relation', beneficiaryData.relation);

        const result = await serverRequest2('POST', `/mvest/update-beneficiary/${beneficiaryId}`, formData);
        if (!result || !result.success) {
          throw new Error(result?.message || 'Failed to update beneficiary');
        }
        return result;
      } catch (error) {
        const errorMessage = extractErrorMessage(error, 'Failed to update beneficiary');
        notifyOnce("error", errorMessage, "MVest Error");
        throw new Error(errorMessage);
      }
    },
  });
}

// Initiate MVest payment
export function useInitiateMVestPayment() {
  return useMutation({
    mutationFn: async (paymentData) => {
      try {
        const memberNumber = getMemberNumber(paymentData.member_number);
        const formData = new FormData();
        formData.append('member_number', memberNumber);
        formData.append('amount', paymentData.amount);
        formData.append('currency', paymentData.currency || '');
        formData.append('platform', 'web');

        const result = await serverRequest2('POST', '/mvest/payment/initiate', formData);
        if (!result || !result.success) {
          throw new Error(result?.message || 'Failed to initiate MVest payment');
        }
        return result;
      } catch (error) {
        const errorMessage = extractErrorMessage(error, 'Failed to initiate MVest payment');
        notifyOnce("error", errorMessage, "Payment Error");
        throw new Error(errorMessage);
      }
    },
  });
}
